// ResourceWarning.js
import React, { useEffect } from 'react';
import './ResourceWarning.css';
import apiUsageTracker from '../apiUsageTracker';

/**
 * Component to display a warning when approaching API usage limits
 * Redesigned to focus on actual limit proximity with no projections
 */
function ResourceWarning({ isVisible, percentage, onClose }) {
  // Get the active warning threshold (highest crossed threshold)
  const activeThreshold = apiUsageTracker.getActiveWarningThreshold();
  
  // Get formatted time remaining until reset
  const timeRemaining = apiUsageTracker.getFormattedTimeRemaining();
  
  // After displaying, mark this threshold as alerted
  useEffect(() => {
    if (isVisible && activeThreshold) {
      apiUsageTracker.markThresholdAlerted(activeThreshold);
    }
  }, [isVisible, activeThreshold]);
  
  // If there's no active threshold, don't show anything
  if (!isVisible || !activeThreshold) return null;
  
  // Determine warning severity based on threshold
  const getSeverity = () => {
    if (percentage >= 95) return 'critical';
    if (percentage >= 90) return 'high';
    return 'moderate';
  };
  
  // Get appropriate warning message based on severity
  const getMessage = () => {
    const severity = getSeverity();
    const actualPercentage = Math.min(100, Math.max(0, Math.round(percentage)));
    
    if (severity === 'critical') {
      return (
        <>
          <strong>Almost at API rate limit:</strong> You've used <strong>{actualPercentage}%</strong> of your hourly allowance. Limits will reset in <strong>{timeRemaining}</strong>.
        </>
      );
    } else if (severity === 'high') {
      return (
        <>
          <strong>Approaching API rate limit:</strong> You've used <strong>{actualPercentage}%</strong> of your hourly allowance. Limits will reset in <strong>{timeRemaining}</strong>.
        </>
      );
    } else {
      return (
        <>
          API usage is at <strong>{actualPercentage}%</strong> of your hourly limit. Limits will reset in <strong>{timeRemaining}</strong>.
        </>
      );
    }
  };
  
  // Get CSS class based on severity
  const getWarningClass = () => {
    const severity = getSeverity();
    if (severity === 'critical') return 'critical';
    if (severity === 'high') return 'high';
    return '';
  };
  
  return (
    <div className={`resource-warning ${getWarningClass()}`}>
      <div className="resource-warning-content">
        <div className="resource-warning-icon">⚠️</div>
        <div className="resource-warning-text">
          <p>{getMessage()}</p>
          <button 
            className="resource-warning-dismiss"
            onClick={onClose}
          >
            Dismiss
          </button>
        </div>
      </div>
      <div className="resource-warning-progress">
        <div 
          className={`resource-warning-progress-bar ${getWarningClass()}`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
}

export default ResourceWarning;